import { atom } from 'jotai';
import { queryClientAtom } from 'jotai-tanstack-query';

import {
  updateLocationSalesPreferencesMutationAtom,
  UpdateLocationSalesPreferencesInput,
} from '../_graphql/mutationUpdateLocationSalesPreferences';
import {
  locationSalesPreferencesQueryAtom,
  LocationSalesPreferencesData,
} from '../_graphql/queryLocationSalesPreferences';

import { $facilityId } from './locations.state';

export const $locationSalesPreferences = locationSalesPreferencesQueryAtom(
  (get) => {
    const facilityId = get($facilityId);
    return {
      variables: { facilityId: facilityId ?? '' },
      skip: !facilityId,
    };
  },
);

export type PhotoInstruction = {
  id: string;
  instruction: string;
  isRequired: boolean;
};

// Form state for editing
export interface LocationSalesPreferencesFormState {
  defaultWeightDisplayUnit: string;
  defaultPort: string;
  enableDefaultPrinting: boolean;
  skipScalePayment: boolean;
  askForDeletionConfirmation: boolean;
  photoInstructions: PhotoInstruction[];
}

const defaultFormState: LocationSalesPreferencesFormState = {
  defaultWeightDisplayUnit: 'PRICE_UNIT',
  defaultPort: 'california_port',
  enableDefaultPrinting: true,
  skipScalePayment: true,
  askForDeletionConfirmation: true,
  photoInstructions: [],
};

function queryDataToFormState(
  data: LocationSalesPreferencesData | undefined,
): LocationSalesPreferencesFormState {
  if (!data) return { ...defaultFormState };
  return {
    defaultWeightDisplayUnit: data.defaultWeightDisplayUnit,
    defaultPort: data.defaultPort,
    enableDefaultPrinting: data.enableDefaultPrinting,
    skipScalePayment: data.skipScalePayment,
    askForDeletionConfirmation: data.askForDeletionConfirmation,
    photoInstructions: data.photoInstructions.map((p) => ({ ...p })),
  };
}

export const $locationSalesPreferencesOriginal = atom((get) => {
  const query = get($locationSalesPreferences);
  return queryDataToFormState(query.data);
});

export const $locationSalesPreferencesLocalChanges = atom<
  Partial<LocationSalesPreferencesFormState>
>({});

export const $locationSalesPreferencesFormState = atom((get) => {
  const original = get($locationSalesPreferencesOriginal);
  const local = get($locationSalesPreferencesLocalChanges);
  const merged: LocationSalesPreferencesFormState = {
    ...original,
    ...local,
  };
  if (local.photoInstructions !== undefined) {
    merged.photoInstructions = local.photoInstructions;
  }
  return merged;
});

// Photo instructions management
const $nextIdCounter = atom(1);

export const $photoInstructionsWithNewRow = atom<PhotoInstruction[]>((get) => {
  const formState = get($locationSalesPreferencesFormState);
  const nextId = get($nextIdCounter);
  return [
    ...formState.photoInstructions,
    { id: `new_${nextId}`, instruction: '', isRequired: false },
  ];
});

// Update form fields
export const $updateLocationSalesPreferencesForm = atom(
  null,
  (get, set, update: Partial<LocationSalesPreferencesFormState>) => {
    const currentLocal = get($locationSalesPreferencesLocalChanges);
    set($locationSalesPreferencesLocalChanges, { ...currentLocal, ...update });
  },
);

export const $updatePhotoInstruction = atom(
  null,
  (get, set, params: { id: string; updates: Partial<PhotoInstruction> }) => {
    const formState = get($locationSalesPreferencesFormState);
    const currentLocal = get($locationSalesPreferencesLocalChanges);
    const updatedInstructions = formState.photoInstructions.map((p) =>
      p.id === params.id ? { ...p, ...params.updates } : p,
    );
    set($locationSalesPreferencesLocalChanges, {
      ...currentLocal,
      photoInstructions: updatedInstructions,
    });
  },
);

export const $addPhotoInstruction = atom(
  null,
  (get, set, params: { id: string; instruction: string }) => {
    const formState = get($locationSalesPreferencesFormState);
    const currentLocal = get($locationSalesPreferencesLocalChanges);
    const newInstruction: PhotoInstruction = {
      id: params.id,
      instruction: params.instruction,
      isRequired: false,
    };
    set($locationSalesPreferencesLocalChanges, {
      ...currentLocal,
      photoInstructions: [...formState.photoInstructions, newInstruction],
    });
    set($nextIdCounter, get($nextIdCounter) + 1);
  },
);

export const $removePhotoInstruction = atom(null, (get, set, id: string) => {
  const formState = get($locationSalesPreferencesFormState);
  const currentLocal = get($locationSalesPreferencesLocalChanges);
  set($locationSalesPreferencesLocalChanges, {
    ...currentLocal,
    photoInstructions: formState.photoInstructions.filter((p) => p.id !== id),
  });
});

// Check if form has unsaved changes
export const $hasUnsavedSalesPreferencesChanges = atom((get) => {
  const local = get($locationSalesPreferencesLocalChanges);
  return Object.keys(local).length > 0;
});

// Discard changes - reset local overrides
export const $discardLocationSalesPreferencesChanges = atom(
  null,
  (_get, set) => {
    set($locationSalesPreferencesLocalChanges, {});
  },
);

const LOCATION_SALES_PREFERENCES_QUERY_KEY =
  'query.settings.locationSalesPreferences';

// Mutation atom for saving
export const $updateRemoteLocationSalesPreferences =
  updateLocationSalesPreferencesMutationAtom({
    onSuccess: (get, set) => {
      set($locationSalesPreferencesLocalChanges, {});
      const queryClient = get(queryClientAtom);
      queryClient.invalidateQueries({
        queryKey: [LOCATION_SALES_PREFERENCES_QUERY_KEY],
      });
    },
  });

// Save action
export const $saveLocationSalesPreferences = atom(null, async (get, set) => {
  const facilityId = get($facilityId);
  const formState = get($locationSalesPreferencesFormState);

  if (!facilityId) return;

  const input: UpdateLocationSalesPreferencesInput = {
    facilityId,
    ...formState,
  };

  await set($updateRemoteLocationSalesPreferences, input);
});
